// Reporting helpers for Stage 1: marginal aggregates per axis, coverage
// metric, allocation preview (without doing the actual draw), and Markdown
// report builder. All pure functions — testable from Node (Vitest) without
// any DOM or Solid imports.
//
// UI-facing strings use plain-language German per CONTEXT.md glossary
// (Bevölkerungsgruppe instead of Stratum, Merkmal instead of Achse);
// statistics terms appear in parentheses for auditors. Audit-JSON schema
// fields stay unchanged — that is a machine format.

import type { AgeBand } from './age-bands';
import { bucketize, largestRemainderAllocation } from './stratify';
import type { Stage1AuditDoc, StratumResult } from './types';

// ---------------------------------------------------------------------------
// Marginal aggregates per axis
// ---------------------------------------------------------------------------

/**
 * One axis-value bucket aggregated across all cross-product strata that
 * share the same value for the chosen axis.
 *
 * Example: with axes = [district, gender], `MarginalBucket` for axis="gender"
 * value="female" sums the n_h_pool/target/actual across all
 * (district=*, gender=female) strata.
 */
export interface MarginalBucket {
  /** Axis name. */
  axis: string;
  /** Value of that axis for this bucket (e.g. 'female', '01-zentrum'). */
  value: string;
  /** Sum of n_h_pool across all strata with this axis-value. */
  pool: number;
  /** Sum of n_h_target. */
  target: number;
  /** Sum of n_h_actual. */
  actual: number;
}

/** Per-axis grouping of MarginalBuckets, codepoint-sorted by value. */
export interface MarginalsForAxis {
  axis: string;
  buckets: MarginalBucket[];
  /** Sum of pool across all buckets — equals total pool. */
  totalPool: number;
  /** Sum of target across all buckets — equals total target. */
  totalTarget: number;
  /** Sum of actual across all buckets — equals total actual. */
  totalActual: number;
}

/**
 * For each axis in the strata cross-product, fold the cross-product cells
 * into per-axis-value totals. Buckets are codepoint-sorted by value for
 * deterministic display order.
 */
export function marginalAggregates(
  strata: StratumResult[],
  axes: string[],
): MarginalsForAxis[] {
  const out: MarginalsForAxis[] = [];
  for (const axis of axes) {
    const map = new Map<string, MarginalBucket>();
    for (const s of strata) {
      const value = s.key[axis];
      if (value === undefined) continue;
      let b = map.get(value);
      if (b === undefined) {
        b = { axis, value, pool: 0, target: 0, actual: 0 };
        map.set(value, b);
      }
      b.pool += s.n_h_pool;
      b.target += s.n_h_target;
      b.actual += s.n_h_actual;
    }
    const buckets = [...map.values()].sort((a, b) =>
      a.value < b.value ? -1 : a.value > b.value ? 1 : 0,
    );
    const totalPool = buckets.reduce((a, b) => a + b.pool, 0);
    const totalTarget = buckets.reduce((a, b) => a + b.target, 0);
    const totalActual = buckets.reduce((a, b) => a + b.actual, 0);
    out.push({ axis, buckets, totalPool, totalTarget, totalActual });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Underfill sorting
// ---------------------------------------------------------------------------

/**
 * Sort strata by descending allocation gap (n_h_target - n_h_actual).
 *
 * The largest gap (most-underfilled stratum) appears first. Ties are broken
 * by codepoint-ascending stratum-key ordering (deterministic, matches the
 * existing `stratum_sort` audit field). Returns a new array — input is not
 * mutated.
 *
 * Note: this function does NOT filter. If callers want only underfilled
 * strata they should pre-filter (e.g. `strata.filter(s => s.underfilled)`)
 * before calling. Strata with `n_h_target <= n_h_actual` will simply have
 * a non-positive gap and end up at the bottom of the result.
 */
export function sortUnderfillsByGap(strata: StratumResult[]): StratumResult[] {
  return [...strata].sort((a, b) => {
    const gapA = a.n_h_target - a.n_h_actual;
    const gapB = b.n_h_target - b.n_h_actual;
    if (gapB !== gapA) return gapB - gapA;
    // Tie-break on codepoint-ascending key for determinism.
    const keyA = JSON.stringify(a.key);
    const keyB = JSON.stringify(b.key);
    return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
  });
}

// ---------------------------------------------------------------------------
// Coverage metric
// ---------------------------------------------------------------------------

export interface CoverageMetric {
  /** Total number of distinct strata that exist in the input pool. */
  totalStrata: number;
  /** Number of strata with at least 1 person actually drawn. */
  coveredStrata: number;
  /** Number of strata where n_h_actual < n_h_target. */
  underfilledStrata: number;
  /** coveredStrata / totalStrata in [0,1]. NaN if totalStrata === 0. */
  coverageRatio: number;
}

export function coverageMetric(strata: StratumResult[]): CoverageMetric {
  const total = strata.length;
  let covered = 0;
  let under = 0;
  for (const s of strata) {
    if (s.n_h_actual > 0) covered++;
    if (s.underfilled) under++;
  }
  return {
    totalStrata: total,
    coveredStrata: covered,
    underfilledStrata: under,
    coverageRatio: total === 0 ? Number.NaN : covered / total,
  };
}

// ---------------------------------------------------------------------------
// Pre-run allocation preview
// ---------------------------------------------------------------------------

/**
 * One row of the pre-run preview: each occupied stratum with its
 * computed Hamilton allocation BEFORE actually drawing. No RNG, no shuffle.
 *
 * Same fields as StratumResult except n_h_actual is missing — we don't know
 * actual until we draw.
 */
export interface PreviewRow {
  key: Record<string, string>;
  n_h_pool: number;
  n_h_target: number;
  /** True iff `n_h_target > n_h_pool` would force underfill at draw time. */
  wouldUnderfill: boolean;
}

export interface AllocationPreview {
  rows: PreviewRow[];
  /** Number of strata expected to receive zero allocation. */
  zeroAllocationStrata: number;
  /** Number of strata expected to underfill (rare — only with empty cells). */
  underfillStrata: number;
  /** sum(n_h_target) — should equal targetN. */
  totalTarget: number;
}

/**
 * Compute the would-be Hamilton allocation for the given inputs WITHOUT
 * actually shuffling or drawing. Cheap (no PRNG calls); used for the
 * pre-run preview UI.
 */
export function previewAllocation(
  rows: Record<string, string>[],
  axes: string[],
  targetN: number,
): AllocationPreview {
  if (targetN < 0) {
    throw new Error('Stichprobengröße (targetN) muss >= 0 sein.');
  }
  if (targetN > rows.length) {
    throw new Error(
      `Eingangs-Pool hat nur ${rows.length} Personen, mehr als das ist nicht ziehbar (angefragt: ${targetN}).`,
    );
  }

  const buckets = bucketize(rows, axes);
  const stratumKeys = [...buckets.keys()].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const stratumIndexLists = stratumKeys.map((k) => buckets.get(k)!);
  const stratumSizes = stratumIndexLists.map((l) => l.length);
  const allocation = largestRemainderAllocation(stratumKeys, stratumSizes, targetN);

  const previewRows: PreviewRow[] = [];
  let zeros = 0;
  let unders = 0;
  for (let i = 0; i < stratumKeys.length; i++) {
    const k = stratumKeys[i]!;
    const target = allocation[i]!;
    const pool = stratumSizes[i]!;
    const wouldUnderfill = target > pool;
    if (target === 0) zeros++;
    if (wouldUnderfill) unders++;
    previewRows.push({
      key: JSON.parse(k).reduce(
        (acc: Record<string, string>, [a, v]: [string, string]) => {
          acc[a] = v;
          return acc;
        },
        {} as Record<string, string>,
      ),
      n_h_pool: pool,
      n_h_target: target,
      wouldUnderfill,
    });
  }

  return {
    rows: previewRows,
    zeroAllocationStrata: zeros,
    underfillStrata: unders,
    totalTarget: previewRows.reduce((a, b) => a + b.n_h_target, 0),
  };
}

// ---------------------------------------------------------------------------
// Info-only bands report (Issue #62)
// ---------------------------------------------------------------------------

/**
 * One row of the "Nicht in Auswahl einbezogen" report — the pool counts and
 * a hypothetical proportional Soll for a display-only band. The hypothetical
 * Soll answers the question "if this band were drawn proportionally, how
 * many people would be in scope?" — useful for sanity-checking the pool's
 * minor population without selecting them.
 */
export interface InfoOnlyBandsReportRow {
  /** Band label (also the value in the altersgruppe column). */
  label: string;
  /** Number of CSV rows whose ageBandColumn equals this label. */
  poolCount: number;
  /** Math.round(targetN * poolCount / totalPoolSize), or 0 when totalPoolSize=0. */
  hypotheticalSoll: number;
}

/**
 * Build a per-band breakdown of pool counts + hypothetical Soll for every
 * band currently in `display-only` mode. Bands in `selection` mode are
 * skipped — they appear in the regular result tables.
 *
 * Pure: does not mutate inputs. Returns rows in input bands order.
 */
export function infoOnlyBandsReport(
  rows: Record<string, string>[],
  bands: readonly AgeBand[],
  ageBandColumn: string,
  targetN: number,
  totalPoolSize: number,
): InfoOnlyBandsReportRow[] {
  const out: InfoOnlyBandsReportRow[] = [];
  for (const band of bands) {
    if (band.mode !== 'display-only') continue;
    let poolCount = 0;
    for (const r of rows) {
      if (r[ageBandColumn] === band.label) poolCount++;
    }
    const hypotheticalSoll =
      totalPoolSize > 0 ? Math.round((targetN * poolCount) / totalPoolSize) : 0;
    out.push({ label: band.label, poolCount, hypotheticalSoll });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Markdown report builder
// ---------------------------------------------------------------------------

/**
 * Build a human-readable Markdown report from a Stage1AuditDoc.
 *
 * Contains: header (Verfahren-Name placeholder), parameters (seed, axes,
 * target, actual), coverage metric, marginal aggregates per axis, full
 * strata table, underfill list, signature footer.
 *
 * NO PII (no person IDs in the report, just aggregate group counts).
 */
export function stage1ToMarkdownReport(audit: Stage1AuditDoc): string {
  const margs = marginalAggregates(audit.strata, audit.stratification_axes);
  const cov = coverageMetric(audit.strata);

  const lines: string[] = [];
  lines.push('# Versand-Auswahl — Bericht');
  lines.push('');
  lines.push(`**Erstellt:** ${audit.timestamp_iso}`);
  lines.push(`**Eingangs-Datei:** \`${audit.input_csv_filename}\` (${audit.input_csv_size_bytes} Bytes, SHA-256 \`${audit.input_csv_sha256}\`)`);
  lines.push(`**Algorithmus:** ${audit.algorithm_version} (PRNG ${audit.prng})`);
  lines.push('');

  lines.push('## Parameter');
  lines.push('');
  lines.push(`- **Pool-Größe:** ${audit.pool_size}`);
  lines.push(`- **Ziel-Stichprobengröße:** ${audit.target_n}`);
  lines.push(`- **Tatsächlich gezogen:** ${audit.actual_n}`);
  lines.push(`- **Aufteilungs-Merkmale (Stratifikations-Achsen):** ${audit.stratification_axes.length === 0 ? '(keine — einfache Zufallsstichprobe)' : audit.stratification_axes.join(', ')}`);
  lines.push(`- **Seed:** \`${audit.seed}\` (${audit.seed_source === 'user' ? 'manuell vorgegeben' : 'Default Unix-Sekunde'})`);
  lines.push(`- **Laufzeit:** ${audit.duration_ms} ms`);
  lines.push('');

  // Issue #64: Bemessung. Only renders when the audit doc actually carries
  // a sample-size proposal (i.e. the user touched the calculator). Manual-N
  // runs produce 0.4 docs without this section.
  if (audit.sample_size_proposal) {
    const p = audit.sample_size_proposal;
    const outreachLabel =
      p.outreach === 'mail-only'
        ? 'Nur Briefe'
        : p.outreach === 'mail-plus-phone'
          ? 'Briefe + Telefon-Nachfasser'
          : 'Eigene Rücklaufquote';
    const fmtPct = (r: number): string => {
      const v = r * 100;
      return Math.abs(v - Math.round(v)) < 1e-9 ? `${Math.round(v)} %` : `${v.toFixed(1)} %`;
    };
    const minPct = fmtPct(p.response_rate_min);
    const maxPct = fmtPct(p.response_rate_max);
    lines.push('## Bemessung der Stichprobe');
    lines.push('');
    lines.push(`- **Ziel-Panelgröße:** ${p.panel_size}`);
    lines.push(`- **Outreach-Methode:** ${outreachLabel} (${minPct}–${maxPct} Rücklauf)`);
    lines.push(`- **Sicherheitspuffer:** × ${p.safety_factor}`);
    lines.push(`- **Vorschlag:** ${p.recommended} Briefe (Range ${p.range[0]}–${p.range[1]})`);
    if (p.manually_overridden) {
      lines.push(
        `- **Stichprobengröße:** ${audit.target_n} (manuell überschrieben — Vorschlag war ${p.recommended})`,
      );
    } else {
      lines.push(`- **Stichprobengröße:** ${audit.target_n} (Vorschlag übernommen)`);
    }
    lines.push('');
  }

  // Issue #62: derived columns documentation. Only renders when the audit
  // doc actually carries the optional field — older 0.2 docs are silent.
  if (audit.derived_columns && Object.keys(audit.derived_columns).length > 0) {
    lines.push('## Berechnete Spalten');
    lines.push('');
    for (const [col, info] of Object.entries(audit.derived_columns)) {
      lines.push(`### ${col}`);
      lines.push('');
      lines.push(`**Quelle:** ${info.source}`);
      lines.push('');
      lines.push(`**Beschreibung:** ${info.description}`);
      if (info.bands && info.bands.length > 0) {
        lines.push('');
        lines.push('| Band | Min | Max | Modus |');
        lines.push('| --- | ---: | ---: | --- |');
        for (const b of info.bands) {
          const max = b.max === null ? 'offen' : String(b.max);
          lines.push(`| ${b.label} | ${b.min} | ${max} | ${b.mode} |`);
        }
      }
      lines.push('');
    }
  }

  lines.push('## Gruppen-Abdeckung');
  lines.push('');
  const covPct = Number.isNaN(cov.coverageRatio) ? 0 : Math.round(cov.coverageRatio * 1000) / 10;
  lines.push(`**${cov.coveredStrata} von ${cov.totalStrata} Bevölkerungsgruppen abgedeckt** (${covPct} %).`);
  if (cov.underfilledStrata > 0) {
    lines.push('');
    lines.push(`Davon **${cov.underfilledStrata} unterbesetzt** (Pool zu klein für die proportionale Soll-Allokation).`);
  }
  lines.push('');

  if (margs.length > 0) {
    lines.push('## Verteilung pro Merkmal (Marginale)');
    lines.push('');
    for (const m of margs) {
      lines.push(`### ${m.axis}`);
      lines.push('');
      lines.push('| Wert | Pool | Soll | Ist | Soll-% | Ist-% |');
      lines.push('| --- | ---: | ---: | ---: | ---: | ---: |');
      for (const b of m.buckets) {
        const sollPct = m.totalTarget === 0 ? '–' : `${((b.target / m.totalTarget) * 100).toFixed(1)} %`;
        const istPct = m.totalActual === 0 ? '–' : `${((b.actual / m.totalActual) * 100).toFixed(1)} %`;
        lines.push(`| ${b.value} | ${b.pool} | ${b.target} | ${b.actual} | ${sollPct} | ${istPct} |`);
      }
      lines.push('');
    }
  }

  // Issue #62: "Nicht in Auswahl einbezogen" — aggregate the forced-zero
  // strata's pool counts and compute a hypothetical proportional Soll
  // (had they participated). Only renders when the audit doc carries
  // forced_zero_strata and at least one stratum is actually marked.
  if (audit.forced_zero_strata && audit.forced_zero_strata.length > 0) {
    const perBand = new Map<string, number>();
    for (const s of audit.strata) {
      if (s.forced_zero !== true) continue;
      const label = s.key.altersgruppe ?? '(unbenannt)';
      perBand.set(label, (perBand.get(label) ?? 0) + s.n_h_pool);
    }
    if (perBand.size > 0) {
      lines.push('## Nicht in Auswahl einbezogen');
      lines.push('');
      lines.push('| Band | Im Pool | Hypothetisch (Soll-Proportion) |');
      lines.push('| --- | ---: | ---: |');
      const labels = [...perBand.keys()].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
      for (const label of labels) {
        const sum = perBand.get(label)!;
        const hypothetical =
          audit.pool_size > 0 ? Math.round((audit.target_n * sum) / audit.pool_size) : 0;
        lines.push(`| ${label} | ${sum} | ${hypothetical} |`);
      }
      lines.push('');
      lines.push(
        '_Diese Personen wurden nicht gezogen — eigene Verfahrenswege denkbar (z.B. Kinderrat)._',
      );
      lines.push('');
    }
  }

  lines.push('## Detail-Tabelle (Bevölkerungsgruppen, Cross-Product)');
  lines.push('');
  lines.push('| Bevölkerungsgruppe (Stratum) | Pool | Soll | Ist | Status |');
  lines.push('| --- | ---: | ---: | ---: | --- |');
  for (const s of audit.strata) {
    const stratumLabel = Object.keys(s.key).length === 0
      ? '(gesamt)'
      : Object.entries(s.key).map(([k, v]) => `${k}=${v}`).join(', ');
    const status = s.forced_zero ? 'nur Anzeige' : s.underfilled ? 'unterbesetzt' : 'ok';
    lines.push(`| ${stratumLabel} | ${s.n_h_pool} | ${s.n_h_target} | ${s.n_h_actual} | ${status} |`);
  }
  lines.push('');

  if (audit.warnings.length > 0) {
    lines.push('## Warnungen');
    lines.push('');
    for (const w of audit.warnings) {
      lines.push(`- ${w}`);
    }
    lines.push('');
  }

  lines.push('## Signatur');
  lines.push('');
  if (audit.signature && audit.public_key && audit.signature_algo) {
    lines.push(`- **Algorithmus:** ${audit.signature_algo}`);
    lines.push(`- **Öffentlicher Schlüssel:** \`${audit.public_key}\``);
    lines.push(`- **Signatur:** \`${audit.signature}\``);
    lines.push('');
    lines.push('Die Signatur deckt die canonicalisierte JSON-Form des Audit-Dokuments ab — Eingaben, Allokation, **und die `selected_indices`-Liste**. Eine Manipulation am Ergebnis macht die Signatur ungültig.');
  } else {
    lines.push('_(noch nicht signiert)_');
  }
  lines.push('');

  return lines.join('\n');
}
